import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/help/shortcuts')({
  component: ShortcutsHelp,
});

const ORIGIN_NOTE = 'Replace https://ectopic.ha.diamonddrake.com with your install if different.';

const ECTOPIC_BODY = `{
  "kind": "ectopic",
  "occurred_at": "REPLACE_WITH_CURRENT_DATE",
  "severity": 2,
  "ectopic_count": 1,
  "tags": {}
}`;

const REFLUX_BODY = `{
  "kind": "reflux",
  "occurred_at": "REPLACE_WITH_CURRENT_DATE",
  "severity": 3,
  "reflux_location": "throat",
  "woke_from_sleep": true,
  "tags": {}
}`;

const IBS_BODY = `{
  "kind": "ibs",
  "occurred_at": "REPLACE_WITH_CURRENT_DATE",
  "severity": 2,
  "ibs_kind": "pain",
  "tags": {}
}`;

const PAIN_BODY = `{
  "kind": "pain",
  "occurred_at": "REPLACE_WITH_CURRENT_DATE",
  "severity": 3,
  "pain_location": "back",
  "tags": {}
}`;

const ROLLUP_BODY = `{
  "local_date": "REPLACE_WITH_LOCAL_DATE",
  "tz": "America/New_York",
  "overall_score": 3,
  "ectopic_score": 3,
  "reflux_score": 2,
  "ibs_score": 4,
  "pain_score": 3
}`;

function ShortcutsHelp() {
  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">iOS Shortcuts setup</h1>
        <p className="text-sm text-muted-foreground">
          Add Siri voice commands like "Hey Siri, log an ectopic" and a 9pm rollup reminder.
        </p>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Prerequisite — mint an API key</CardTitle>
          <CardDescription>
            Each Shortcut authenticates with a Bearer token. Mint one and copy it — it's only
            shown once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/settings">Open Settings → API keys</Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>The basic shape</CardTitle>
          <CardDescription>{ORIGIN_NOTE}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <p>Every Shortcut is the same pattern:</p>
          <ol className="ml-5 list-decimal space-y-1.5">
            <li>
              <strong>UUID</strong> action — generates a fresh idempotency key.
            </li>
            <li>
              <strong>Current Date</strong> action with format
              <code className="mx-1 rounded bg-muted px-1 font-mono text-xs">
                ISO 8601
              </code>
              including time zone.
            </li>
            <li>
              <strong>Text</strong> action — the JSON body with magic strings replaced via
              "Replace Text" actions before posting.
            </li>
            <li>
              <strong>Get Contents of URL</strong> action:
              <ul className="ml-5 list-disc">
                <li>
                  URL: <code className="font-mono text-xs">/api/incidents</code> or{' '}
                  <code className="font-mono text-xs">/api/rollups</code>
                </li>
                <li>Method: POST</li>
                <li>
                  Headers:{' '}
                  <code className="font-mono text-xs">Authorization: Bearer YOUR_TOKEN</code>,{' '}
                  <code className="font-mono text-xs">Content-Type: application/json</code>,{' '}
                  <code className="font-mono text-xs">Idempotency-Key: &lt;UUID&gt;</code>
                </li>
                <li>Request body: the Text from step 3.</li>
              </ul>
            </li>
          </ol>
          <p>
            Bind it to a Siri phrase, the Home Screen, an Apple Watch complication, or a time
            automation.
          </p>
        </CardContent>
      </Card>

      <ExampleCard
        title="Log an ectopic"
        endpoint="/api/incidents"
        body={ECTOPIC_BODY}
        notes="Replace occurred_at with the Current Date variable formatted ISO 8601."
      />
      <ExampleCard
        title="Log a reflux event"
        endpoint="/api/incidents"
        body={REFLUX_BODY}
        notes="Adjust reflux_location to throat/chest/both."
      />
      <ExampleCard
        title="Log an IBS event"
        endpoint="/api/incidents"
        body={IBS_BODY}
        notes="ibs_kind: pain | bloating | urgency | stool. Drop bristol_scale for non-stool."
      />
      <ExampleCard
        title="Log a pain event"
        endpoint="/api/incidents"
        body={PAIN_BODY}
        notes="pain_location: chest | back | head | neck | abdomen | joints | other."
      />
      <ExampleCard
        title="9pm daily rollup"
        endpoint="/api/rollups"
        body={ROLLUP_BODY}
        notes="Bind to a Time of Day Automation at 21:00. local_date is the current date in YYYY-MM-DD. tz must match an IANA name."
      />

      <Card>
        <CardHeader>
          <CardTitle>iOS quirks</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p>
            Background Sync isn't available on iOS Safari, so the in-app outbox drains when the
            tab becomes visible or comes online. Siri Shortcuts post directly, so they aren't
            affected.
          </p>
          <p>
            The server dedupes any Idempotency-Key it has seen within ~7 days. If a Shortcut
            misfires twice with the same UUID, only the first write wins.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ExampleCard({
  title,
  endpoint,
  body,
  notes,
}: {
  title: string;
  endpoint: string;
  body: string;
  notes: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          POST <code className="font-mono">{endpoint}</code>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <CodeBlock>{body}</CodeBlock>
        <p className="text-xs text-muted-foreground">{notes}</p>
      </CardContent>
    </Card>
  );
}

function CodeBlock({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <pre
      className={cn(
        'overflow-x-auto rounded-md border border-border bg-muted/50 p-3 font-mono text-xs',
        className,
      )}
    >
      {children}
    </pre>
  );
}
